import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface UseQueueCountOptions {
  branchId?: string;
  barberId?: string;
}

interface QueueCountResult {
  count: number;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to get real-time queue count for a branch or specific barber
 */
export function useQueueCount({ branchId, barberId }: UseQueueCountOptions): QueueCountResult {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!branchId) {
      setLoading(false);
      return;
    }

    // Get start of today
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build query for active tickets
    const queuesRef = collection(db, 'queues');
    let q;

    if (barberId) {
      // Count tickets for specific barber
      q = query(
        queuesRef,
        where('branchId', '==', branchId),
        where('barberId', '==', barberId),
        where('status', 'in', ['waiting', 'notified', 'arrived'])
      );
    } else {
      // Count all active tickets in branch
      q = query(
        queuesRef,
        where('branchId', '==', branchId),
        where('status', 'in', ['waiting', 'notified', 'arrived'])
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setCount(snapshot.size);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error getting queue count:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [branchId, barberId]);

  return { count, loading, error };
}

/**
 * Hook to get queue counts for multiple branches at once
 */
export function useMultipleBranchQueueCounts(branchIds: string[]): Record<string, QueueCountResult> {
  const [counts, setCounts] = useState<Record<string, QueueCountResult>>({});

  useEffect(() => {
    if (!branchIds.length) return;

    const unsubscribers: (() => void)[] = [];

    branchIds.forEach((branchId) => {
      const queuesRef = collection(db, 'queues');
      const q = query(
        queuesRef,
        where('branchId', '==', branchId),
        where('status', 'in', ['waiting', 'notified', 'arrived'])
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setCounts((prev) => ({
            ...prev,
            [branchId]: {
              count: snapshot.size,
              loading: false,
              error: null,
            },
          }));
        },
        (err) => {
          setCounts((prev) => ({
            ...prev,
            [branchId]: {
              count: 0,
              loading: false,
              error: err,
            },
          }));
        }
      );

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [branchIds.join(',')]);

  return counts;
}

/**
 * Get color class based on queue count
 */
export function getQueueCountColor(count: number): string {
  if (count === 0) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (count <= 2) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (count <= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
}

export default useQueueCount;
